import { isIosDevice } from './device'
import { getCorsFixedUrl } from './dom'

const MIME_TYPES: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  mp4: 'video/mp4',
  webm: 'video/webm',
  mov: 'video/quicktime',
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  ogg: 'audio/ogg',
  glb: 'model/gltf-binary',
  gltf: 'model/gltf+json',
  stl: 'model/stl',
}

function getMimeTypeFromFileName(fileName: string): string | undefined {
  const ext = fileName.split('.').pop()?.toLowerCase()
  return ext ? MIME_TYPES[ext] : undefined
}

export interface DownloadMediaFileOptions {
  url: string
  fileName: string
  /** Inferred from fileName extension when omitted. */
  mimeType?: string
  /**
   * Called with download progress as a percentage (0-100) when the response
   * exposes a `Content-Length` header. When omitted, no progress is tracked
   * and the response body is consumed in one shot.
   */
  onProgress?: (progress: number) => void
}

function isInlineUrl(url: string) {
  return url.startsWith('data:') || url.startsWith('blob:')
}

function getDownloadFetchUrl(url: string) {
  return isInlineUrl(url) ? url : getCorsFixedUrl(url)
}

async function readBodyWithProgress(
  response: Response,
  onProgress?: (progress: number) => void,
): Promise<Blob> {
  const contentLength = response.headers.get('Content-Length')
  const total = contentLength ? parseInt(contentLength, 10) : 0
  const contentType = response.headers.get('Content-Type') || ''

  if (!onProgress || !response.body || total <= 0) {
    return await response.blob()
  }

  const reader = response.body.getReader()
  const chunks: BlobPart[] = []
  let received = 0

  onProgress(0)

  for (let result = await reader.read(); !result.done; result = await reader.read()) {
    if (result.value) {
      chunks.push(result.value)
      received += result.value.length
      const progress = Math.min(Math.round((received / total) * 100), 100)
      onProgress(progress)
    }
  }

  onProgress(100)

  return new Blob(chunks, contentType ? { type: contentType } : undefined)
}

function downloadViaAnchor(blobUrl: string, fileName: string) {
  const a = document.createElement('a')
  a.href = blobUrl
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

/**
 * Downloads a media file with iOS-aware handling.
 *
 * On iOS, the standard `<a download>` approach opens the file in a new tab
 * instead of triggering a download. This function detects iOS and uses
 * `navigator.share()` to present the native share sheet, which allows
 * saving the file to Photos.
 *
 * On non-iOS devices (or if sharing is not supported), falls back to a
 * fetch-and-anchor download.
 *
 * The MIME type is inferred from the fileName extension when not explicitly
 * provided, falling back to the response Content-Type.
 */
export async function downloadMediaFile({
  url,
  fileName,
  mimeType: explicitMimeType,
  onProgress,
}: DownloadMediaFileOptions): Promise<void> {
  const mimeType = explicitMimeType ?? getMimeTypeFromFileName(fileName)
  const fetchUrl = getDownloadFetchUrl(url)
  const response = await fetch(fetchUrl)

  if (!response.ok) {
    throw new Error(`Failed to fetch: HTTP ${response.status} ${response.statusText}`)
  }

  const blob = await readBodyWithProgress(response, onProgress)

  if (isIosDevice()) {
    const file = new File([blob], fileName, { type: mimeType ?? blob.type })
    if (navigator.canShare?.({ files: [file] })) {
      let didShare = false
      try {
        await navigator.share({ files: [file] })
        didShare = true
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          return
        }
        if (error instanceof DOMException && error.name === 'NotAllowedError') {
          // Fall through to the anchor path when the share sheet is blocked by browser policy.
        } else {
          throw error
        }
      }
      if (didShare) {
        return
      }
    }
  }

  const blobUrl = URL.createObjectURL(mimeType ? new Blob([blob], { type: mimeType }) : blob)
  downloadViaAnchor(blobUrl, fileName)
  URL.revokeObjectURL(blobUrl)
}
